import { addMoney, multiplyMoneyByQuantity } from "./money";
import type {
  BillingCadence,
  MoneyMinor,
  Quotation,
  QuotationLineItem,
  QuotationTotals,
  QuotationTotalsGroup,
} from "./model";

export function calculateLineAmountMinor(
  lineItem: QuotationLineItem,
): MoneyMinor | null {
  if (lineItem.valueStatus === "tbd" || lineItem.unitPriceMinor === null) {
    return null;
  }

  return multiplyMoneyByQuantity(lineItem.unitPriceMinor, lineItem.quantityMilli);
}

function calculateGroup(
  lineItems: readonly QuotationLineItem[],
  cadence: BillingCadence,
): QuotationTotalsGroup {
  const includedItems = lineItems.filter(
    (lineItem) =>
      lineItem.billingCadence === cadence && (!lineItem.optional || lineItem.included),
  );
  const knownAmounts: MoneyMinor[] = [];
  const unknownLineIds: string[] = [];

  for (const lineItem of includedItems) {
    const amount = calculateLineAmountMinor(lineItem);
    if (amount === null) {
      unknownLineIds.push(lineItem.id);
    } else {
      knownAmounts.push(amount);
    }
  }

  const knownSubtotalMinor = addMoney(knownAmounts);

  return {
    knownSubtotalMinor,
    authoritativeSubtotalMinor: unknownLineIds.length === 0 ? knownSubtotalMinor : null,
    includedLineCount: includedItems.length,
    unknownLineIds,
  };
}

export function calculateQuotationTotals(quotation: Quotation): QuotationTotals {
  const recurring = calculateGroup(quotation.lineItems, "recurring");
  const oneTime = calculateGroup(quotation.lineItems, "one-time");
  const includedAdjustments = quotation.adjustments.filter(
    (adjustment) => adjustment.included,
  );
  const hasUnknownAdjustment = includedAdjustments.some(
    (adjustment) => adjustment.valueStatus === "tbd" || adjustment.amountMinor === null,
  );
  const adjustmentValues: MoneyMinor[] = [];

  for (const adjustment of includedAdjustments) {
    if (adjustment.valueStatus === "tbd" || adjustment.amountMinor === null) {
      continue;
    }

    adjustmentValues.push(
      adjustment.kind === "discount" ? -adjustment.amountMinor : adjustment.amountMinor,
    );
  }

  const adjustmentTotalMinor = hasUnknownAdjustment ? null : addMoney(adjustmentValues);
  const hasUnknownIncludedValues =
    recurring.authoritativeSubtotalMinor === null ||
    oneTime.authoritativeSubtotalMinor === null ||
    adjustmentTotalMinor === null;
  const grandTotalMinor = hasUnknownIncludedValues
    ? null
    : addMoney([
        recurring.authoritativeSubtotalMinor ?? 0,
        oneTime.authoritativeSubtotalMinor ?? 0,
        adjustmentTotalMinor ?? 0,
      ]);

  return {
    recurring,
    oneTime,
    adjustmentTotalMinor,
    grandTotalMinor,
    hasUnknownIncludedValues,
  };
}
